import { create } from 'zustand'
import type { ChatAction } from '@/api'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  actions?: ChatAction[]
  isStreaming?: boolean
  activeToolCall?: string | null
}

interface ChatState {
  messages: ChatMessage[]
  sessionId: string | null
  isLoading: boolean

  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string
  appendToMessage: (id: string, text: string) => void
  finalizeMessage: (id: string) => void
  setToolIndicator: (id: string, toolName: string | null) => void
  clearMessages: () => void
  setSessionId: (id: string | null) => void
  setLoading: (loading: boolean) => void
}

/**
 * Generate a unique ID for messages
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export const useChatStore = create<ChatState>((set) => ({
  messages: [],
  sessionId: null,
  isLoading: false,

  addMessage: (message) => {
    const id = generateId()
    set((state) => ({
      messages: [
        ...state.messages,
        {
          ...message,
          id,
          timestamp: new Date(),
        },
      ],
    }))
    return id
  },

  appendToMessage: (id, text) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, content: msg.content + text } : msg,
      ),
    })),

  finalizeMessage: (id) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id
          ? { ...msg, isStreaming: false, activeToolCall: null }
          : msg,
      ),
    })),

  setToolIndicator: (id, toolName) =>
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, activeToolCall: toolName } : msg,
      ),
    })),

  clearMessages: () =>
    set({
      messages: [],
      isLoading: false,
    }),

  setSessionId: (id) =>
    set({
      sessionId: id,
    }),

  setLoading: (loading) =>
    set({
      isLoading: loading,
    }),
}))
